import { ParamMap, Params } from '@angular/router';
import {
  BuildingMode,
  BuildingMode2,
  CaseType,
  CaseType2,
  MessageDtoQuestionType,
  MessageFilterCriteriaDto,
  MessageFilterCriteriaDtoCaseType,
  PersonMode,
  PersonMode2,
  PredefinedMessageFilterDto,
  PredefinedMessageFilterDtoTargetView
} from '../services/api.clients.generated';

export interface MessageFilterState {
  search: string;
  buildingMode: BuildingMode;
  buildingIds: string[];
  caseId: string;
  townGroupIds: string[];
  personMode: BuildingMode;
  personIds: string[];
  caseType?: CaseType;
  questionType?: MessageDtoQuestionType;
}

export const DEFAULT_FILTER_MODE = BuildingMode.ALL;
export const FILTER_ID_QUERY_PARAM = 'filterId';

export function emptyMessageFilterState(): MessageFilterState {
  return {
    search: '',
    buildingMode: DEFAULT_FILTER_MODE,
    buildingIds: [],
    caseId: '',
    townGroupIds: [],
    personMode: DEFAULT_FILTER_MODE,
    personIds: [],
    caseType: undefined,
    questionType: undefined
  };
}

export function readMessageFilterState(
  paramMap: ParamMap,
  predefinedFilters: PredefinedMessageFilterDto[] = [],
  targetView?: PredefinedMessageFilterDtoTargetView
): MessageFilterState {
  const filterId = readFilterId(paramMap);
  const resolvedFilter = filterId
    ? predefinedFilters.find(filter =>
      filter.identifier === filterId &&
      (!targetView || filter.targetView === targetView))
    : undefined;

  const baseState = resolvedFilter
    ? stateFromPredefinedFilter(resolvedFilter)
    : emptyMessageFilterState();

  return {
    ...baseState,
    ...readExplicitMessageFilterOverrides(paramMap)
  };
}

export function readExplicitMessageFilterOverrides(paramMap: ParamMap): Partial<MessageFilterState> {
  const state: Partial<MessageFilterState> = {};

  if (paramMap.has('search')) {
    state.search = normalizeText(paramMap.get('search'));
  }
  if (paramMap.has('buildingMode')) {
    state.buildingMode = parseFilterMode(paramMap.get('buildingMode'));
  }
  if (paramMap.has('buildingId')) {
    state.buildingIds = readValues(paramMap, 'buildingId');
  }
  if (paramMap.has('caseId')) {
    state.caseId = normalizeText(paramMap.get('caseId'));
  }
  if (paramMap.has('townGroupId')) {
    state.townGroupIds = readValues(paramMap, 'townGroupId');
  }
  if (paramMap.has('personMode')) {
    state.personMode = parseFilterMode(paramMap.get('personMode'));
  }
  if (paramMap.has('personId')) {
    state.personIds = readValues(paramMap, 'personId');
  }
  if (paramMap.has('caseType')) {
    state.caseType = parseCaseType(paramMap.get('caseType'));
  }
  if (paramMap.has('questionType')) {
    state.questionType = parseQuestionType(paramMap.get('questionType'));
  }

  return state;
}

export function readExplicitMessageFilterStateFromUri(paramMap: ParamMap): MessageFilterState {
  return {
    search: normalizeText(paramMap.get('search')),
    buildingMode: parseFilterMode(paramMap.get('buildingMode')),
    buildingIds: readValues(paramMap, 'buildingId'),
    caseId: normalizeText(paramMap.get('caseId')),
    townGroupIds: readValues(paramMap, 'townGroupId'),
    personMode: parseFilterMode(paramMap.get('personMode')),
    personIds: readValues(paramMap, 'personId'),
    caseType: parseCaseType(paramMap.get('caseType')),
    questionType: parseQuestionType(paramMap.get('questionType'))
  };
}

export function buildMessageFilterQueryParams(state: MessageFilterState): Params {
  const queryParams: Params = {};

  if (state.search) {
    queryParams['search'] = state.search;
  }

  if (state.buildingMode !== DEFAULT_FILTER_MODE) {
    queryParams['buildingMode'] = state.buildingMode;
  }
  if (state.buildingMode === BuildingMode.SELECTED_VALUES && state.buildingIds.length > 0) {
    queryParams['buildingId'] = state.buildingIds;
  }

  if (state.caseId) {
    queryParams['caseId'] = state.caseId;
  }

  if (state.townGroupIds.length > 0) {
    queryParams['townGroupId'] = state.townGroupIds;
  }

  if (state.personMode !== DEFAULT_FILTER_MODE) {
    queryParams['personMode'] = state.personMode;
  }
  if (state.personMode === BuildingMode.SELECTED_VALUES && state.personIds.length > 0) {
    queryParams['personId'] = state.personIds;
  }

  if (state.caseType) {
    queryParams['caseType'] = state.caseType;
  }
  if (state.questionType) {
    queryParams['questionType'] = state.questionType;
  }

  return queryParams;
}

export function stateFromPredefinedFilter(filter: PredefinedMessageFilterDto): MessageFilterState {
  return criteriaToFilterState(filter.criteria);
}

export function routeForPredefinedFilter(filter: PredefinedMessageFilterDto): { path: string; queryParams: Params } {
  const path = filter.targetView === PredefinedMessageFilterDtoTargetView.OPEN_MESSAGES ? '/open-messages' : '/messages';
  return {
    path,
    queryParams: {
      [FILTER_ID_QUERY_PARAM]: filter.identifier
    }
  };
}

export function readFilterId(paramMap: ParamMap): string | undefined {
  const filterId = normalizeText(paramMap.get(FILTER_ID_QUERY_PARAM));
  return filterId || undefined;
}

export function criteriaToFilterState(criteria?: MessageFilterCriteriaDto | null): MessageFilterState {
  return {
    search: normalizeText(criteria?.query),
    buildingMode: parseFilterMode(criteria?.building?.mode ?? undefined),
    buildingIds: (criteria?.building?.values ?? []).filter(Boolean),
    caseId: normalizeText(criteria?.caseId),
    townGroupIds: (criteria?.townGroupIds ?? []).filter(Boolean),
    personMode: parseFilterMode(criteria?.people?.mode ?? undefined),
    personIds: (criteria?.people?.values ?? []).filter(Boolean),
    caseType: criteria?.caseType ? parseCaseType(criteria.caseType) : undefined,
    questionType: parseQuestionType(criteria?.questionType)
  };
}

export function valuesToInputValue(values: string[]): string {
  return values.join(', ');
}

export function inputValueToValues(value: string): string[] {
  return value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
}

export function toMessagesBuildingMode(mode: BuildingMode): BuildingMode {
  return mode;
}

export function toMessagesPersonMode(mode: BuildingMode): PersonMode {
  return mode as unknown as PersonMode;
}

export function toOpenBuildingMode(mode: BuildingMode): BuildingMode2 {
  return mode as unknown as BuildingMode2;
}

export function toOpenPersonMode(mode: BuildingMode): PersonMode2 {
  return mode as unknown as PersonMode2;
}

export function toOpenCaseType(caseType?: CaseType): CaseType2 | undefined {
  return caseType as unknown as CaseType2 | undefined;
}

function parseFilterMode(value?: string | null): BuildingMode {
  if (value && Object.values(BuildingMode).includes(value as BuildingMode)) {
    return value as BuildingMode;
  }
  return DEFAULT_FILTER_MODE;
}

function parseCaseType(value?: string | null): CaseType | undefined {
  if (value && Object.values(CaseType).includes(value as CaseType)) {
    return value as CaseType;
  }
  if (value && Object.values(MessageFilterCriteriaDtoCaseType).includes(value as MessageFilterCriteriaDtoCaseType)) {
    return value as unknown as CaseType;
  }
  return undefined;
}

function parseQuestionType(value?: string | null): MessageDtoQuestionType | undefined {
  if (!value) {
    return undefined;
  }
  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }
  if (Object.values(MessageDtoQuestionType).includes(normalized as MessageDtoQuestionType)) {
    return normalized as MessageDtoQuestionType;
  }
  return undefined;
}

function readValues(paramMap: ParamMap, key: string): string[] {
  return paramMap.getAll(key)
    .map(value => value.trim())
    .filter(Boolean);
}

function normalizeText(value?: string | null): string {
  return value?.trim() ?? '';
}
